import { Users } from 'lucide-react'
import type { AccountType } from '@olimhub/shared'
import bearSvg from '@/assets/icons/animals/bear.svg'
import catSvg from '@/assets/icons/animals/cat.svg'
import deerSvg from '@/assets/icons/animals/deer.svg'
import elephantSvg from '@/assets/icons/animals/elephant.svg'
import foxSvg from '@/assets/icons/animals/fox.svg'
import hedgehogSvg from '@/assets/icons/animals/hedgehog.svg'
import koalaSvg from '@/assets/icons/animals/koala.svg'
import lionSvg from '@/assets/icons/animals/lion.svg'
import owlSvg from '@/assets/icons/animals/owl.svg'
import pandaSvg from '@/assets/icons/animals/panda.svg'
import rabbitSvg from '@/assets/icons/animals/rabbit.svg'
import wolfSvg from '@/assets/icons/animals/wolf.svg'

const ANIMAL_SVGS: Record<string, string> = {
  bear: bearSvg,
  cat: catSvg,
  deer: deerSvg,
  elephant: elephantSvg,
  fox: foxSvg,
  hedgehog: hedgehogSvg,
  koala: koalaSvg,
  lion: lionSvg,
  owl: owlSvg,
  panda: pandaSvg,
  rabbit: rabbitSvg,
  wolf: wolfSvg,
}

const AVATAR_COLORS = [
  '#2D6BE4',
  '#E8A838',
  '#16A34A',
  '#9333ea',
  '#db2777',
  '#0891b2',
  '#ca8a04',
  '#dc2626',
]

interface AvatarProps {
  name: string
  avatarUrl?: string | null
  animalAvatar?: string | null
  // Group accounts (Phase B1) get a small overlay badge — no established
  // overlay pattern existed in DESIGN.md before this, so this introduces one
  // (filled-circle icon, bottom-end corner) rather than inventing something
  // inconsistent with the existing badge visual language (BadgePill's
  // FilledCircle treatment).
  accountType?: AccountType
  size?: 'sm' | 'md' | 'lg'
  className?: string
  style?: React.CSSProperties
}

const SIZE_MAP: Record<NonNullable<AvatarProps['size']>, number> = {
  sm: 32,
  md: 40,
  lg: 72,
}

const FONT_SIZE_MAP: Record<NonNullable<AvatarProps['size']>, number> = {
  sm: 12,
  md: 16,
  lg: 28,
}

function GroupOverlay({ px }: { px: number }) {
  const badgeSize = Math.max(14, Math.round(px * 0.4))
  const iconSize = Math.round(badgeSize * 0.6)
  return (
    <span
      style={{
        position: 'absolute',
        insetInlineEnd: -2,
        insetBlockEnd: -2,
        width: badgeSize,
        height: badgeSize,
        borderRadius: '50%',
        background: 'var(--brand)',
        border: '2px solid var(--surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Users size={iconSize} color="#fff" strokeWidth={2.5} />
    </span>
  )
}

function AvatarInner({ name, avatarUrl, animalAvatar, size = 'md', className, style }: Omit<AvatarProps, 'accountType'>) {
  const px = SIZE_MAP[size]
  const fs = FONT_SIZE_MAP[size]

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={className}
        style={{
          width: px,
          height: px,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          display: 'block',
          ...style,
        }}
      />
    )
  }

  if (animalAvatar && ANIMAL_SVGS[animalAvatar]) {
    return (
      <div
        className={className}
        style={{
          width: px,
          height: px,
          borderRadius: '50%',
          background: 'var(--surface-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          ...style,
        }}
      >
        <img
          src={ANIMAL_SVGS[animalAvatar]}
          alt={name}
          style={{
            width: '70%',
            height: '70%',
            objectFit: 'contain',
          }}
        />
      </div>
    )
  }

  const firstChar = name.trim().charAt(0) || '?'
  const colorIndex = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length
  const bg = AVATAR_COLORS[colorIndex]

  return (
    <div
      className={className}
      style={{
        width: px,
        height: px,
        borderRadius: '50%',
        background: bg,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: fs,
        fontFamily: 'var(--font-sans)',
        flexShrink: 0,
        userSelect: 'none',
        ...style,
      }}
    >
      {firstChar.toUpperCase()}
    </div>
  )
}

export function Avatar({ accountType, ...rest }: AvatarProps) {
  if (accountType !== 'group') return <AvatarInner {...rest} />

  const px = SIZE_MAP[rest.size ?? 'md']
  return (
    <div style={{ position: 'relative', width: px, height: px, flexShrink: 0 }}>
      <AvatarInner {...rest} />
      <GroupOverlay px={px} />
    </div>
  )
}
